import { forwardRef, useCallback, useState } from 'react'
import type { InputHTMLAttributes } from 'react'
import { cn } from '@/shared/lib/cn'
import { focusRingInvalidClasses } from '@/shared/lib/focusRing'
import { inputBaseClasses, inputVariantClasses } from '@/shared/ui/inputStyles'
import { MicButton } from '@/shared/ui/MicButton'
import { isSpeechSupported } from '@/shared/lib/useSpeechToText'

const REQUIRED_VALIDATION_MSG = 'Preencha este campo.'

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  /** Sem wrapper `<label>` — o componente pai associa rótulo via htmlFor/id (ex.: PasswordInput). */
  bare?: boolean
  /** Variante visual: default (branco) ou muted (mint-soft, sem borda) */
  variant?: 'default' | 'muted'
  /** Exibe botão de microfone (Web Speech API) para ditado. Requer onMicTranscript. */
  withMic?: boolean
  /** Chamado quando o microfone retorna transcrição. Use para anexar ao valor. */
  onMicTranscript?: (text: string) => void
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    label,
    bare = false,
    className,
    variant = 'default',
    onInvalid,
    onInput,
    withMic = false,
    onMicTranscript,
    value,
    onChange,
    disabled,
    readOnly,
    ...props
  },
  ref,
) {
  const [speechError, setSpeechError] = useState<string | null>(null)

  const handleTranscript = useCallback(
    (text: string) => {
      onMicTranscript?.(text)
    },
    [onMicTranscript],
  )

  const showMic =
    withMic && isSpeechSupported() && onMicTranscript && !disabled && !readOnly

  const inputEl = (
    <input
      ref={ref}
      value={value}
      onChange={onChange}
      className={cn(inputBaseClasses, inputVariantClasses[variant], focusRingInvalidClasses, showMic && 'pr-14', className)}
      onInvalid={(e) => {
        const el = e.target as HTMLInputElement
        if (el.validity.valueMissing) el.setCustomValidity(REQUIRED_VALIDATION_MSG)
        onInvalid?.(e)
      }}
      onInput={(e) => {
        ;(e.target as HTMLInputElement).setCustomValidity('')
        onInput?.(e)
      }}
      disabled={disabled}
      readOnly={readOnly}
      {...props}
      autoComplete={
        props.autoComplete ??
        (props.type === 'password' ? undefined : 'nope')
      }
    />
  )

  if (bare) {
    if (showMic) {
      return (
        <div className="relative w-full min-w-0">
          {inputEl}
          <MicButton
            onTranscript={handleTranscript}
            position="top-right"
            onErrorChange={setSpeechError}
          />
          {speechError ? (
            <p className="mt-1 text-[10px] text-[var(--color-wine-strong)]">{speechError}</p>
          ) : null}
        </div>
      )
    }
    return inputEl
  }

  return (
    <label className="flex w-full flex-col gap-2 text-xs font-medium text-[var(--color-text-secondary)]">
      {label ? <span>{label}</span> : null}
      {showMic ? (
        <div className="relative w-full min-w-0">
          {inputEl}
          <MicButton
            onTranscript={handleTranscript}
            position="top-right"
            onErrorChange={setSpeechError}
          />
        </div>
      ) : (
        inputEl
      )}
      {speechError && showMic ? (
        <p className="text-[10px] text-[var(--color-wine-strong)]">{speechError}</p>
      ) : null}
    </label>
  )
})

